import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import React from 'react'

import CopyBox from '@/app/component/atom/copy-box'
import Dialog from '@/app/component/atom/dialog/index'
import IconText from '@/app/component/layout/icon-text'
import { useEnableTotp } from '@/app/module/user/hooks/use-enable-totp'
import { useInitTotp } from '@/app/module/user/hooks/use-init-totp'

import RecoveryCodes from './recovery-codes'

type Props = {
  onClose: () => void
}

const AuthenticationVerification: React.FC<Props> = ({ onClose }) => {
  const [initTotp, { isLoading: isInitting }] = useInitTotp()
  const [enableTotp, { isLoading: isEnabling }] = useEnableTotp()

  const [passcode, setPasscode] = React.useState('')
  const [recoveryCodes, setRecoveryCodes] = React.useState<string[]>([])
  const [showSecret, setShowSecret] = React.useState(false)
  const [totpSecret, setTotpSecret] = React.useState('')
  const [totpQRCode, setTotpQRCode] = React.useState('')

  const isLoading = isInitting || isEnabling

  React.useEffect(() => {
    const load = async () => {
      const data = await initTotp()
      if (data?.secret && data?.png) {
        setTotpSecret(data.secret)
        setTotpQRCode(data.png)
      }
    }

    load()
  }, [initTotp])

  const handleEnableTotp = React.useCallback(async () => {
    if (isLoading) {
      return
    }
    const res = await enableTotp(totpSecret, passcode)
    if (res?.recoveryCodes) setRecoveryCodes(res.recoveryCodes)
  }, [isLoading, passcode, totpSecret, enableTotp])

  const handleClose = React.useCallback(() => {
    if (isLoading) {
      return // don't close when initializing or enabling
    }
    onClose()
  }, [isLoading, onClose])

  return (
    <Dialog fullWidth id="enable-totp-modal" open={true} onClose={handleClose}>
      <DialogTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <IconText>
            <Icon>lock</Icon>
            <Typography className="subscription-delay-title" variant="h6">
              Authentication verification
            </Typography>
          </IconText>
          <IconButton aria-label="Close" onClick={onClose}>
            <Icon>close</Icon>
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        {totpQRCode && recoveryCodes.length === 0 && (
          <div>
            <StepTitle>Step 1: Setup your authentication app</StepTitle>
            {showSecret ? (
              <>
                <Typography>Enter the below code in your authentication app</Typography>
                <Typography>
                  If you prefer to scan a QR code,{' '}
                  <Link href="#" onClick={() => setShowSecret(false)}>
                    click here
                  </Link>
                  .{' '}
                </Typography>
                <div>
                  <CopyBox inputStyle={{ width: '400px' }} title="" text={totpSecret} />
                </div>
              </>
            ) : (
              <>
                <Typography>
                  Scan the image below with your two-factor authentication app, for example Google Authenticator or
                  Authy. If you can’t use a QR code,{' '}
                  <Link href="#" onClick={() => setShowSecret(true)}>
                    enter this code
                  </Link>{' '}
                  instead.
                </Typography>
                <div style={{ textAlign: 'center', margin: '10px 5px 20px' }}>
                  <div
                    style={{
                      width: 'fit-content',
                      padding: '15px',
                      borderRadius: '4px',
                      margin: 'auto',
                      border: '1px solid #a0a0a0',
                    }}
                  >
                    <img src={`data:image/png;base64,${totpQRCode}`} />
                  </div>
                </div>
              </>
            )}
            <div></div>
            <StepTitle>Step 2: Enter the six-digit code from the application</StepTitle>
            <div style={{ marginTop: '10px' }}>
              <FormControl variant="standard">
                <Typography>Enter the six-digit code.</Typography>
                <TextField
                  autoFocus={true}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return
                    handleEnableTotp()
                  }}
                  label="Code"
                  type="number"
                  variant="outlined"
                  value={passcode}
                  onChange={(e: any) => setPasscode(e.currentTarget.value)}
                />
              </FormControl>
            </div>
          </div>
        )}
        {recoveryCodes.length > 0 && (
          <div>
            <Typography>Save your recovery codes</Typography>
            <div>
              <RecoveryCodes recoveryCodes={recoveryCodes} />
            </div>
            <div>
              <Typography>
                If you lose access to your device, you can use recovery codes to access your account instead.
              </Typography>
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {totpQRCode && recoveryCodes.length === 0 && (
          <Button
            color="primary"
            disabled={passcode.length !== 6 || isLoading}
            loading={isEnabling}
            variant="contained"
            onClick={handleEnableTotp}
          >
            Verify
          </Button>
        )}
        {recoveryCodes.length > 0 && (
          <Button variant="contained" color="primary" onClick={onClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

const StepTitle = ({ children }: { children: string }) => (
  <div style={{ marginBottom: '15px' }}>
    <Typography variant="h5">{children}</Typography>
    <Divider />
  </div>
)

export default AuthenticationVerification
